import type { Config } from '@puckeditor/core'

import { generateDefaultProps, generatePuckFields } from '@/lib/puck-schema-generator'
import { registerAllSectionSchemas } from '@/lib/section-schemas'

import NonFsdsEmailVerificationSection from '@/components/auth/puck-auth-sections/non-fsds-email-verification-section'
import NonFsdsForgotPasswordSection from '@/components/auth/puck-auth-sections/non-fsds-forgot-password-section'
import NonFsdsPasswordResetSuccessfulSection from '@/components/auth/puck-auth-sections/non-fsds-password-reset-successful-section'
import NonFsdsResetPasswordSection from '@/components/auth/puck-auth-sections/non-fsds-reset-password-section'
import NonFsdsSignInSection from '@/components/auth/puck-auth-sections/non-fsds-sign-in-section'
import NonFsdsSignUpSection from '@/components/auth/puck-auth-sections/non-fsds-sign-up-section'
import NonFsdsVerifyAccountSection from '@/components/auth/puck-auth-sections/non-fsds-verify-account-section'
import ConsultationForm from '@/components/forms/consultation-form'
import NonFsdsCorporateCustomerSection from '@/components/homepage/non-fsds-sections/non-fsds-corporate-customer-section'
import NonFsdsFaqSection from '@/components/homepage/non-fsds-sections/non-fsds-faq-section'
import NonFsdsFeaturePoweredSection from '@/components/homepage/non-fsds-sections/non-fsds-feature-powered-section'
import NonFsdsFeatureSection from '@/components/homepage/non-fsds-sections/non-fsds-feature-section'
import NonFsdsFeedbackSection from '@/components/homepage/non-fsds-sections/non-fsds-feedback-section'
import NonFsdsHeroSection from '@/components/homepage/non-fsds-sections/non-fsds-hero-section'
import NonFsdsLecturersSection from '@/components/homepage/non-fsds-sections/non-fsds-lecturers-section'
import NonFsdsMoneyBackGuaranteeSection from '@/components/homepage/non-fsds-sections/non-fsds-money-back-guarantee-section'
import NonFsdsProjectSection from '@/components/homepage/non-fsds-sections/non-fsds-project-section'
import NonFsdsStatsSection from '@/components/homepage/non-fsds-sections/non-fsds-stats-section'
import NonFsdsLpCoursePathSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-course-path-section'
import NonFsdsLpCtaSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-cta-section'
import NonFsdsLpFaqSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-faq-section'
import NonFsdsLpHeroSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-hero-section'
import NonFsdsLpOverviewSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-overview-section'
import NonFsdsLpProjectsSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-projects-section'
import NonFsdsLpTestimonialsSection from '@/components/learning-path/non-fsds-lp-sections/non-fsds-lp-testimonials-section'
import {
  ColorFieldRender,
  FooterConfigFieldRender,
  HeaderConfigFieldRender,
  ImageFieldRender,
} from '@/components/puck/puck-custom-fields'
import PuckFooterSection from '@/components/puck/puck-footer-section'
import PuckHeaderSection from '@/components/puck/puck-header-section'
import ContainerSection from '@/components/sections/ContainerSection'
import CorporateCustomer from '@/components/sections/corporate-customer'
import FaqQuestionAnswerSection from '@/components/sections/faq-question-answer-section'
import FeedbackSection from '@/components/sections/feedback-section'
import LecturersSectionV2 from '@/components/sections/lecturers-section-v2'
import MoneyBackGuarantee from '@/components/sections/money-back-guarantee'
import ProjectSection from '@/components/sections/project-section'
import SectionHeroV3 from '@/components/sections/v3/section-hero-v3'
import CoursePathSection from '@/components/sections/v4/course-path-section'
import FeaturePoweredSection from '@/components/sections/v4/feature-powered-section'
import FeatureSection from '@/components/sections/v4/feature-section'
import LearningPathOverviewSection from '@/components/sections/v4/learning-path-overview-section'
import SectionHeroLearningPath from '@/components/sections/v4/section-hero-v4'
import StatsSection from '@/components/sections/v4/stats-section'

import { elementConfigs } from '@/configs/element-configs'
import { NON_FSDS_DEFAULT_LOGO_URL } from '@/configs/non-fsds-config'
import { GENERAL_V1_COMPONENT_KEYS, generalV1PuckComponents } from '@/configs/puck-components/non-fsds-general-v1-config'
import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'


registerAllSectionSchemas()

/** Shared Puck field definition for theme selection. */
const nonFsdsThemeField = {
  label: 'Theme',
  type: 'select' as const,
  options: [
    { label: 'General (Blue)', value: 'general' },
    { label: 'English Academy (Blue/Emerald)', value: 'english-academy' },
    { label: 'Programming (Indigo)', value: 'programming' },
    { label: 'Math (Teal)', value: 'math' },
  ],
}

/**
 * Puck editor configuration mapping existing React section components
 * as Puck components. Each component is registered with its configurable
 * props exposed as Puck fields for the visual editor.
 *
 * Components with elementSchema will have fields auto-generated.
 * Element building blocks (ElementHeading, ElementButton, etc.) are
 * imported from element-configs and spread into components.
 */
export const puckConfig: Config = {
  categories: {
    navigation: {
      title: 'Navigation',
      components: ['PuckHeaderSection', 'PuckFooterSection'],
    },
    heroSections: {
      title: 'Hero Sections',
      components: ['SectionHeroV3', 'SectionHeroLearningPath'],
    },
    layout: {
      title: 'Layout',
      components: ['ElementSection', 'ElementContainer', 'ElementStack', 'ElementGrid'],
    },
    column: {
      title: 'Column',
      components: [
        'ElementColumn1',
        'ElementColumn2',
        'ElementColumn3',
        'ElementColumn4',
        'ElementColumn2_1',
        'ElementColumn1_2',
        'ElementColumn1_2_1',
        'ElementColumn2_1_2',
      ],
    },
    text: {
      title: 'Text',
      components: ['ElementParagraph', 'ElementHeading'],
    },
    actions: {
      title: 'Actions',
      components: ['ElementButton', 'ElementForm', 'ElementLink'],
    },
    media: {
      title: 'Media',
      components: ['ElementEmbed', 'ElementImage', 'ElementVideo'],
    },
    basic: {
      title: 'Basic',
      components: [
        'ElementBadge',
        'ElementBadgeList',
        'ElementBlockquote',
        'ElementCode',
        'ElementCustomHTML',
        'ElementLine',
        'ElementList',
      ],
    },
    content: {
      title: 'Content',
      components: [
        'ContainerSection',
        'StatsSection',
        'CorporateCustomer',
        'MoneyBackGuarantee',
        'ProjectSection',
        'FeatureSection',
        'FeaturePoweredSection',
        'LecturersSectionV2',
        'FeedbackSection',
        'FaqQuestionAnswerSection',
        'LearningPathOverviewSection',
        'CoursePathSection',
      ],
    },
    forms: {
      title: 'Forms',
      components: ['ConsultationForm'],
    },
    nonFsds: {
      title: 'Non-FSDS',
      components: [
        'NonFsdsHeroSection',
        'NonFsdsStatsSection',
        'NonFsdsCorporateCustomerSection',
        'NonFsdsMoneyBackGuaranteeSection',
        'NonFsdsProjectSection',
        'NonFsdsFeatureSection',
        'NonFsdsFeaturePoweredSection',
        'NonFsdsLecturersSection',
        'NonFsdsFeedbackSection',
        'NonFsdsFaqSection',
      ],
    },
    nonFsdsLearningPath: {
      title: 'Learning Path',
      components: [
        'NonFsdsLpHeroSection',
        'NonFsdsLpOverviewSection',
        'NonFsdsLpCoursePathSection',
        'NonFsdsLpProjectsSection',
        'NonFsdsLpTestimonialsSection',
        'NonFsdsLpFaqSection',
        'NonFsdsLpCtaSection',
      ],
    },
    nonFsdsAuth: {
      title: 'Auth',
      components: [
        'NonFsdsSignInSection',
        'NonFsdsSignUpSection',
        'NonFsdsForgotPasswordSection',
        'NonFsdsEmailVerificationSection',
        'NonFsdsVerifyAccountSection',
        'NonFsdsResetPasswordSection',
        'NonFsdsPasswordResetSuccessfulSection',
      ],
    },
    generalV1: {
      title: 'General V1',
      components: [...GENERAL_V1_COMPONENT_KEYS],
    },
  },
  components: {
    // ── Granular Element Building Blocks ──
    ...elementConfigs,

    // ── Pre-built Section Components ──
    PuckHeaderSection: {
      label: 'Header (NavBar)',
      defaultProps: {
        visible: true,
        fallbackLogoUrl: '',
        brandHref: '/',
        brandName: '',
        headerConfig: {
          layoutPreset: 'logo-left',
          sticky: true,
          showBorder: true,
          showLeftSection: true,
          showRightSection: true,
          containerWidth: 'full',
          leftSectionWidth: 'auto',
          rightSectionWidth: 'auto',
          spacing: 'normal',
          navItems: [],
          mobileNavItems: [],
          rightActions: [],
        },
      },
      fields: {
        visible: {
          label: 'Show Header',
          type: 'radio',
          options: [
            { label: 'On', value: true },
            { label: 'Off', value: false },
          ],
        },
        fallbackLogoUrl: {
          label: 'Custom Logo',
          type: 'custom',
          render: ImageFieldRender,
        },
        brandHref: {
          label: 'Brand Link',
          type: 'text',
        },
        brandName: {
          label: 'Brand Name',
          type: 'text',
        },
        headerConfig: {
          label: 'Header Config',
          type: 'custom',
          render: HeaderConfigFieldRender,
        },
      },
      render: ({ visible, fallbackLogoUrl, brandHref, brandName, headerConfig, puck: _puck }) => (
        <PuckHeaderSection
          visible={visible}
          fallbackLogoUrl={fallbackLogoUrl}
          brandHref={brandHref}
          brandName={brandName}
          headerConfig={headerConfig}
        />
      ),
    },
    PuckFooterSection: {
      label: 'Footer',
      defaultProps: {
        visible: true,
        fallbackLogoUrl: '',
        brandHref: '/',
        brandName: '',
        footerConfig: {
          layoutPreset: 'default',
          showLogo: true,
          showLinkGroups: false,
          showSocialLinks: true,
          showCopyright: true,
          showBorder: true,
          containerWidth: 'content',
          spacing: 'normal',
          linkGroups: [],
        },
      },
      fields: {
        visible: {
          label: 'Show Footer',
          type: 'radio',
          options: [
            { label: 'On', value: true },
            { label: 'Off', value: false },
          ],
        },
        fallbackLogoUrl: {
          label: 'Custom Logo',
          type: 'custom',
          render: ImageFieldRender,
        },
        brandHref: {
          label: 'Brand Link',
          type: 'text',
        },
        brandName: {
          label: 'Brand Name',
          type: 'text',
        },
        footerConfig: {
          label: 'Footer Config',
          type: 'custom',
          render: FooterConfigFieldRender,
        },
      },
      render: ({ visible, fallbackLogoUrl, brandHref, brandName, footerConfig, puck: _puck }) => (
        <PuckFooterSection
          visible={visible}
          fallbackLogoUrl={fallbackLogoUrl}
          brandHref={brandHref}
          brandName={brandName}
          footerConfig={footerConfig}
        />
      ),
    },
    ContainerSection: {
      label: 'Container',
      defaultProps: generateDefaultProps('ContainerSection'),
      fields: generatePuckFields('ContainerSection'),
      render: ({ id, className, layout, background, puck: _puck }) => (
        <ContainerSection id={id} className={className} layout={layout} background={background} />
      ),
    },
    SectionHeroV3: {
      label: 'Hero Section',
      defaultProps: generateDefaultProps('SectionHeroV3'),
      fields: generatePuckFields('SectionHeroV3'),
      render: ({
        heading,
        description,
        className,
        bgType,
        bgColor,
        bgGradientFrom,
        bgGradientTo,
        bgGradientDirection,
        bgGradientOpacity,
        bgImage,
        bgVideo,
        overlay,
        bgImagePosition,
        bgImageSize,
        textAlignment,
        puck: _puck,
      }) => (
        <SectionHeroV3
          isCourseEnrolled={false}
          heading={heading}
          description={description}
          className={className}
          bgType={bgType}
          bgColor={bgColor}
          bgGradientFrom={bgGradientFrom}
          bgGradientTo={bgGradientTo}
          bgGradientDirection={bgGradientDirection}
          bgGradientOpacity={bgGradientOpacity}
          bgImage={bgImage}
          bgVideo={bgVideo}
          overlay={overlay}
          bgImagePosition={bgImagePosition}
          bgImageSize={bgImageSize}
          textAlignment={textAlignment}
        />
      ),
    },
    StatsSection: {
      label: 'Stats Section',
      defaultProps: generateDefaultProps('StatsSection'),
      fields: generatePuckFields('StatsSection'),
      render: ({
        className,
        heading,
        subheading,
        headingColor,
        subheadingColor,
        stats,
        columns,
        bgColor,
        bgGradientFrom,
        bgGradientTo,
        numberColor,
        numberSize,
        labelColor,
        labelSize,
        padding,
        minHeight,
        puck: _puck,
      }) => (
        <StatsSection
          className={className}
          heading={heading}
          subheading={subheading}
          headingColor={headingColor}
          subheadingColor={subheadingColor}
          stats={stats}
          columns={columns}
          bgColor={bgColor}
          bgGradientFrom={bgGradientFrom}
          bgGradientTo={bgGradientTo}
          numberColor={numberColor}
          numberSize={numberSize}
          labelColor={labelColor}
          labelSize={labelSize}
          padding={padding}
          minHeight={minHeight}
        />
      ),
    },
    CorporateCustomer: {
      label: 'Corporate Customers',
      defaultProps: generateDefaultProps('CorporateCustomer'),
      fields: generatePuckFields('CorporateCustomer'),
      render: ({
        className,
        heading,
        subheading,
        headingColor,
        subheadingColor,
        logos,
        logoSource,
        logoSize,
        bgColor,
        textColor,
        puck: _puck,
      }) => (
        <CorporateCustomer
          className={className}
          heading={heading}
          subheading={subheading}
          headingColor={headingColor}
          subheadingColor={subheadingColor}
          logos={logos}
          logoSource={logoSource}
          logoSize={logoSize}
          bgColor={bgColor}
          textColor={textColor}
        />
      ),
    },
    MoneyBackGuarantee: {
      label: 'Money Back Guarantee',
      defaultProps: generateDefaultProps('MoneyBackGuarantee'),
      fields: generatePuckFields('MoneyBackGuarantee'),
      render: ({
        className,
        heading,
        description,
        noteText,
        buttonText,
        bgGradientFrom,
        bgGradientTo,
        maxWidth,
        minHeight,
        puck: _puck,
      }) => (
        <MoneyBackGuarantee
          isCourseEnrolled={false}
          className={className}
          heading={heading}
          description={description}
          noteText={noteText}
          buttonText={buttonText}
          bgGradientFrom={bgGradientFrom}
          bgGradientTo={bgGradientTo}
          maxWidth={maxWidth}
          minHeight={minHeight}
        />
      ),
    },
    ProjectSection: {
      label: 'Projects Section',
      defaultProps: generateDefaultProps('ProjectSection'),
      fields: generatePuckFields('ProjectSection'),
      render: ({
        className,
        heading,
        description,
        headingColor,
        descriptionColor,
        bgColor,
        dataSource,
        pageSize,
        projects,
        cardSize,
        autoplay,
        autoplayDelay,
        puck: _puck,
      }) => (
        <ProjectSection
          className={className}
          heading={heading}
          description={description}
          headingColor={headingColor}
          descriptionColor={descriptionColor}
          bgColor={bgColor}
          dataSource={dataSource}
          pageSize={pageSize}
          projects={projects}
          cardSize={cardSize}
          autoplay={autoplay}
          autoplayDelay={autoplayDelay}
        />
      ),
    },
    FeatureSection: {
      label: 'Feature Section',
      defaultProps: generateDefaultProps('FeatureSection'),
      fields: generatePuckFields('FeatureSection'),
      render: ({
        className,
        heading,
        headingColor,
        descriptionColor,
        bgColor,
        dataSource,
        features,
        enableScrollspy,
        puck: _puck,
      }) => (
        <FeatureSection
          className={className}
          heading={heading}
          headingColor={headingColor}
          descriptionColor={descriptionColor}
          bgColor={bgColor}
          dataSource={dataSource}
          features={features}
          enableScrollspy={enableScrollspy}
        />
      ),
    },
    FeaturePoweredSection: {
      label: 'Feature Powered Section',
      defaultProps: generateDefaultProps('FeaturePoweredSection'),
      fields: generatePuckFields('FeaturePoweredSection'),
      render: ({
        className,
        heading,
        headingColor,
        subtitleColor,
        bulletColor,
        bgColor,
        cardBgColor,
        dataSource,
        features,
        columns,
        showComingSoon,
        puck: _puck,
      }) => (
        <FeaturePoweredSection
          className={className}
          heading={heading}
          headingColor={headingColor}
          subtitleColor={subtitleColor}
          bulletColor={bulletColor}
          bgColor={bgColor}
          cardBgColor={cardBgColor}
          dataSource={dataSource}
          features={features}
          columns={columns}
          showComingSoon={showComingSoon}
        />
      ),
    },
    LecturersSectionV2: {
      label: 'Lecturers Section',
      defaultProps: generateDefaultProps('LecturersSectionV2'),
      fields: generatePuckFields('LecturersSectionV2'),
      render: ({
        className,
        heading,
        subheading,
        headingColor,
        subheadingColor,
        dataSource,
        instructors,
        columns,
        bgColor,
        showStatus,
        puck: _puck,
      }) => (
        <LecturersSectionV2
          className={className}
          heading={heading}
          subheading={subheading}
          headingColor={headingColor}
          subheadingColor={subheadingColor}
          dataSource={dataSource}
          instructors={instructors}
          columns={columns}
          bgColor={bgColor}
          showStatus={showStatus}
        />
      ),
    },
    FeedbackSection: {
      label: 'Feedback Section',
      defaultProps: generateDefaultProps('FeedbackSection'),
      fields: generatePuckFields('FeedbackSection'),
      render: ({
        className,
        heading,
        subtitle,
        headingColor,
        subtitleColor,
        bgColor,
        cardColumns,
        cardStyle,
        dataSource,
        pageSize,
        feedbacks,
        autoplay,
        autoplayDelay,
        loop,
        showNavigation,
        showViewAllButton,
        viewAllUrl,
        viewAllText,
        puck: _puck,
      }) => (
        <FeedbackSection
          className={className}
          heading={heading}
          subtitle={subtitle}
          headingColor={headingColor}
          subtitleColor={subtitleColor}
          bgColor={bgColor}
          cardColumns={cardColumns}
          cardStyle={cardStyle}
          dataSource={dataSource}
          pageSize={pageSize}
          feedbacks={feedbacks}
          autoplay={autoplay}
          autoplayDelay={autoplayDelay}
          loop={loop}
          showNavigation={showNavigation}
          showViewAllButton={showViewAllButton}
          viewAllUrl={viewAllUrl}
          viewAllText={viewAllText}
        />
      ),
    },
    FaqQuestionAnswerSection: {
      label: 'FAQ Section',
      defaultProps: generateDefaultProps('FaqQuestionAnswerSection'),
      fields: generatePuckFields('FaqQuestionAnswerSection'),
      render: ({ className, heading, dataSource, pageSize, category, faqs, expandMode, puck: _puck }) => (
        <FaqQuestionAnswerSection
          className={className}
          heading={heading}
          dataSource={dataSource}
          pageSize={pageSize}
          category={category}
          faqs={faqs}
          expandMode={expandMode}
        />
      ),
    },
    SectionHeroLearningPath: {
      label: 'Learning Path Hero',
      defaultProps: generateDefaultProps('SectionHeroLearningPath'),
      fields: generatePuckFields('SectionHeroLearningPath'),
      render: ({
        heading,
        description,
        className,
        bgType,
        bgColor,
        bgGradientFrom,
        bgGradientTo,
        bgGradientDirection,
        bgGradientOpacity,
        bgImage,
        bgVideo,
        overlay,
        bgImagePosition,
        bgImageSize,
        textAlignment,
        minHeight,
        containerWidth,
        puck: _puck,
      }) => (
        <SectionHeroLearningPath
          heading={heading}
          description={description}
          className={className}
          bgType={bgType}
          bgColor={bgColor}
          bgGradientFrom={bgGradientFrom}
          bgGradientTo={bgGradientTo}
          bgGradientDirection={bgGradientDirection}
          bgGradientOpacity={bgGradientOpacity}
          bgImage={bgImage}
          bgVideo={bgVideo}
          overlay={overlay}
          bgImagePosition={bgImagePosition}
          bgImageSize={bgImageSize}
          textAlignment={textAlignment}
          minHeight={minHeight}
          containerWidth={containerWidth}
        />
      ),
    },
    LearningPathOverviewSection: {
      label: 'Learning Path Overview',
      defaultProps: generateDefaultProps('LearningPathOverviewSection'),
      fields: generatePuckFields('LearningPathOverviewSection'),
      render: ({
        className,
        dataSource,
        heading,
        methodsHeading,
        methods,
        methodsImage,
        previewImages,
        buttonText,
        buttonUrl,
        // Layout
        sectionPadding,
        methodsLayout,
        methodsGap,
        previewLayout,
        // Element details
        iconStyle,
        showMethodsImage,
        showPreviewImages,
        showButton,
        // Colors
        headingColor,
        methodsHeadingColor,
        methodTitleColor,
        methodDescColor,
        bgColor,
        iconBgColor,
        iconColor,
        buttonBgColor,
        buttonTextColor,
        puck: _puck,
      }) => (
        <LearningPathOverviewSection
          className={className}
          dataSource={dataSource}
          heading={heading}
          methodsHeading={methodsHeading}
          methods={methods}
          methodsImage={methodsImage}
          previewImages={previewImages}
          buttonText={buttonText}
          buttonUrl={buttonUrl}
          sectionPadding={sectionPadding}
          methodsLayout={methodsLayout}
          methodsGap={methodsGap}
          previewLayout={previewLayout}
          iconStyle={iconStyle}
          showMethodsImage={showMethodsImage}
          showPreviewImages={showPreviewImages}
          showButton={showButton}
          headingColor={headingColor}
          methodsHeadingColor={methodsHeadingColor}
          methodTitleColor={methodTitleColor}
          methodDescColor={methodDescColor}
          bgColor={bgColor}
          iconBgColor={iconBgColor}
          iconColor={iconColor}
          buttonBgColor={buttonBgColor}
          buttonTextColor={buttonTextColor}
        />
      ),
    },
    CoursePathSection: {
      label: 'Course Path Section',
      defaultProps: generateDefaultProps('CoursePathSection'),
      fields: generatePuckFields('CoursePathSection'),
      render: ({
        className,
        heading,
        subheading,
        headingColor,
        subheadingColor,
        descriptionColor,
        bgColor,
        dataSource,
        courses,
        enableScrollspy,
        puck: _puck,
      }) => (
        <CoursePathSection
          className={className}
          heading={heading}
          subheading={subheading}
          headingColor={headingColor}
          subheadingColor={subheadingColor}
          descriptionColor={descriptionColor}
          bgColor={bgColor}
          dataSource={dataSource}
          courses={courses}
          enableScrollspy={enableScrollspy}
        />
      ),
    },
    NonFsdsHeroSection: {
      label: 'Hero',
      defaultProps: {
        title: 'Build practical AI and data skills for your team',
        description: 'A learning experience with the same proven journey: roadmap, projects, mentors, and outcomes.',
        ctaStart: 'Start Learning',
        ctaContinue: 'Continue Learning',
        ctaExplore: 'Explore curriculum',
        ctaStartUrl: TEMPLATE_DEFAULT_URLS.myCourses,
        ctaContinueUrl: '/learning-path',
        ctaExploreUrl: '/learning-path',
        trustItems: [
          { value: 'Live mentorship' },
          { value: 'Project-first curriculum' },
          { value: 'Career-focused outcomes' },
        ],
        isCourseEnrolled: false,
        isEnglish: true,
        heroImage: '/images/laptop.jpg',
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'textarea' },
        description: { label: 'Description', type: 'textarea' },
        ctaStart: { label: 'CTA Start', type: 'text' },
        ctaContinue: { label: 'CTA Continue', type: 'text' },
        ctaExplore: { label: 'CTA Explore', type: 'text' },
        ctaStartUrl: { label: 'Start CTA Link URL', type: 'text' },
        ctaContinueUrl: { label: 'Continue CTA Link URL', type: 'text' },
        ctaExploreUrl: { label: 'Explore CTA Link URL', type: 'text' },
        trustItems: {
          label: 'Trust Items',
          type: 'array',
          arrayFields: { value: { label: 'Item', type: 'text' } },
          getItemSummary: (item: Record<string, string>) => item.value || 'Item',
        },
        heroImage: { label: 'Hero Image', type: 'custom', render: ImageFieldRender },
        theme: nonFsdsThemeField,
      },
      render: ({
        title,
        description,
        ctaStart,
        ctaContinue,
        ctaExplore,
        ctaStartUrl,
        ctaContinueUrl,
        ctaExploreUrl,
        trustItems,
        heroImage,
        theme,
        puck: _puck,
      }) => (
        <NonFsdsHeroSection
          title={title}
          description={description}
          ctaStart={ctaStart}
          ctaContinue={ctaContinue}
          ctaExplore={ctaExplore}
          ctaStartUrl={ctaStartUrl}
          ctaContinueUrl={ctaContinueUrl}
          ctaExploreUrl={ctaExploreUrl}
          heroImage={heroImage}
          trustItems={
            Array.isArray(trustItems)
              ? trustItems.map((item: { value: string } | string) => (typeof item === 'string' ? item : item.value))
              : []
          }
          isCourseEnrolled={false}
          isEnglish={true}
          theme={theme}
        />
      ),
    },
    NonFsdsStatsSection: {
      label: 'Stats',
      defaultProps: {
        stats: [
          { label: 'Learners', value: '20k+' },
          { label: 'Projects built', value: '150+' },
          { label: 'Mentors', value: '35+' },
          { label: 'Completion rate', value: '89%' },
        ],
        theme: 'general',
      },
      fields: {
        stats: {
          label: 'Stats',
          type: 'array',
          arrayFields: {
            label: { label: 'Label', type: 'text' },
            value: { label: 'Value', type: 'text' },
          },
          getItemSummary: (item: Record<string, string>) => `${item.value} ${item.label}`,
        },
        theme: nonFsdsThemeField,
      },
      render: ({ stats, theme, puck: _puck }) => <NonFsdsStatsSection stats={stats} theme={theme} />,
    },
    NonFsdsCorporateCustomerSection: {
      label: 'Corporate Customers',
      defaultProps: {
        title: 'Trusted by teams from',
        partners: [
          { value: 'Tech startup' },
          { value: 'E-commerce' },
          { value: 'EdTech' },
          { value: 'Fintech' },
          { value: 'Enterprise' },
        ],
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        partners: {
          label: 'Partners',
          type: 'array',
          arrayFields: { value: { label: 'Partner', type: 'text' } },
          getItemSummary: (item: Record<string, string>) => item.value || 'Partner',
        },
        theme: nonFsdsThemeField,
      },
      render: ({ title, partners, theme, puck: _puck }) => (
        <NonFsdsCorporateCustomerSection
          title={title}
          partners={
            Array.isArray(partners)
              ? partners.map((item: { value: string } | string) => (typeof item === 'string' ? item : item.value))
              : []
          }
          theme={theme}
        />
      ),
    },
    NonFsdsMoneyBackGuaranteeSection: {
      label: 'Money Back Guarantee',
      defaultProps: {
        title: '30-day money-back guarantee',
        description: 'Try the program with confidence. If it is not a fit, get a full refund in 30 days.',
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        description: { label: 'Description', type: 'textarea' },
        theme: nonFsdsThemeField,
      },
      render: ({ title, description, theme, puck: _puck }) => (
        <NonFsdsMoneyBackGuaranteeSection title={title} description={description} theme={theme} />
      ),
    },
    NonFsdsProjectSection: {
      label: 'Projects',
      defaultProps: {
        title: 'Portfolio-ready projects',
        description: 'Hands-on, review-based delivery with clear evaluation criteria.',
        projects: [
          { value: 'Customer churn prediction' },
          { value: 'Sales forecasting dashboard' },
          { value: 'Recommendation engine prototype' },
        ],
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        description: { label: 'Description', type: 'textarea' },
        projects: {
          label: 'Projects',
          type: 'array',
          arrayFields: { value: { label: 'Project', type: 'text' } },
          getItemSummary: (item: Record<string, string>) => item.value || 'Project',
        },
        theme: nonFsdsThemeField,
      },
      render: ({ title, description, projects, theme, puck: _puck }) => (
        <NonFsdsProjectSection
          title={title}
          description={description}
          projects={
            Array.isArray(projects)
              ? projects.map((item: { value: string } | string) => (typeof item === 'string' ? item : item.value))
              : []
          }
          theme={theme}
        />
      ),
    },
    NonFsdsFeatureSection: {
      label: 'Features',
      defaultProps: {
        features: [{ value: 'Lộ trình rõ ràng' }, { value: 'Mentor phản hồi nhanh' }, { value: 'Bài tập bám thực tế' }],
        theme: 'general',
      },
      fields: {
        features: {
          label: 'Features',
          type: 'array',
          arrayFields: { value: { label: 'Feature', type: 'text' } },
          getItemSummary: (item: Record<string, string>) => item.value || 'Feature',
        },
        theme: nonFsdsThemeField,
      },
      render: ({ features, theme, puck: _puck }) => (
        <NonFsdsFeatureSection
          features={
            Array.isArray(features)
              ? features.map((item: { value: string } | string) => (typeof item === 'string' ? item : item.value))
              : []
          }
          theme={theme}
        />
      ),
    },
    NonFsdsFeaturePoweredSection: {
      label: 'Feature Powered',
      defaultProps: {
        title: 'Powered learning stack',
        description: 'Mentor office hours, project rubrics, and progress analytics in one workflow.',
        items: [],
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        description: { label: 'Description', type: 'textarea' },
        theme: nonFsdsThemeField,
      },
      render: ({ title, description, items, theme, puck: _puck }) => (
        <NonFsdsFeaturePoweredSection title={title} description={description} items={items || []} theme={theme} />
      ),
    },
    NonFsdsLecturersSection: {
      label: 'Lecturers',
      defaultProps: {
        title: 'Expert instructors',
        instructors: [],
        isEnglish: true,
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        theme: nonFsdsThemeField,
      },
      render: ({ title, instructors, isEnglish, theme, puck: _puck }) => (
        <NonFsdsLecturersSection title={title} instructors={instructors || []} isEnglish={isEnglish ?? true} theme={theme} />
      ),
    },
    NonFsdsFeedbackSection: {
      label: 'Feedback',
      defaultProps: {
        title: 'What learners say',
        quotes: [
          {
            value: 'The weekly mentor feedback helped me fix real project issues fast.',
            avatar: '/images/avatar.png',
          },
          {
            value: 'I stopped learning in isolation and started improving with a real peer network.',
            avatar: '/images/avatar.png',
          },
        ],
        isEnglish: true,
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        quotes: {
          label: 'Quotes',
          type: 'array',
          defaultItemProps: {
            value: 'New quote goes here.',
            avatar: '',
          },
          arrayFields: {
            value: { label: 'Quote', type: 'textarea' },
            avatar: { label: 'Avatar', type: 'custom', render: ImageFieldRender },
          },
          getItemSummary: (item: Record<string, string>) => (item.value || 'Quote').slice(0, 40) + '...',
        },
        theme: nonFsdsThemeField,
      },
      render: ({ title, quotes, isEnglish, theme, puck: _puck }) => (
        <NonFsdsFeedbackSection
          title={title}
          quotes={Array.isArray(quotes) ? quotes : []}
          isEnglish={isEnglish ?? true}
          theme={theme}
        />
      ),
    },
    NonFsdsFaqSection: {
      label: 'FAQ',
      defaultProps: {
        title: 'Frequently asked questions',
        faqs: [
          {
            id: 'faq-1',
            q: 'How is this different from self-paced video courses?',
            a: 'You get mentor guidance, structured milestones, and project reviews.',
          },
        ],
        theme: 'general',
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        faqs: {
          label: 'FAQs',
          type: 'array',
          arrayFields: {
            id: { label: 'ID', type: 'text' },
            q: { label: 'Question', type: 'text' },
            a: { label: 'Answer', type: 'textarea' },
          },
          getItemSummary: (item: Record<string, string>) => item.q || 'Question',
        },
        theme: nonFsdsThemeField,
      },
      render: ({ title, faqs, theme, puck: _puck }) => <NonFsdsFaqSection title={title} faqs={faqs} theme={theme} />,
    },
    NonFsdsLpHeroSection: {
      label: 'LP Hero',
      defaultProps: {
        title: 'Your roadmap to becoming a Full Stack Data Scientist',
        subtitle:
          'A structured, mentor-guided journey from fundamentals to production-ready AI and data engineering skills.',
        badge: 'Learning Path',
        ctaEnroll: 'Start Learning',
        ctaCurriculum: 'View Curriculum',
        ctaEnrollUrl: TEMPLATE_DEFAULT_URLS.myCourses,
        ctaCurriculumUrl: '#course-path',
        highlights: [
          { value: '3', label: 'Courses' },
          { value: '~12', label: 'Months' },
          { value: '35+', label: 'Mentors' },
          { value: '✓', label: 'Certificate' },
        ],
      },
      fields: {
        title: { label: 'Title', type: 'textarea' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        badge: { label: 'Badge Text', type: 'text' },
        ctaEnroll: { label: 'CTA Enroll', type: 'text' },
        ctaCurriculum: { label: 'CTA Curriculum', type: 'text' },
        ctaEnrollUrl: { label: 'Enroll CTA Link URL', type: 'text' },
        ctaCurriculumUrl: { label: 'Curriculum CTA Link URL', type: 'text' },
        highlights: {
          label: 'Highlights',
          type: 'array',
          arrayFields: {
            value: { label: 'Value', type: 'text' },
            label: { label: 'Label', type: 'text' },
          },
          getItemSummary: (item: Record<string, string>) => `${item.value} ${item.label}`,
        },
      },
      render: ({
        title,
        subtitle,
        badge,
        ctaEnroll,
        ctaCurriculum,
        ctaEnrollUrl,
        ctaCurriculumUrl,
        highlights,
        puck: _puck,
      }) => (
        <NonFsdsLpHeroSection
          title={title}
          subtitle={subtitle}
          badge={badge}
          ctaEnroll={ctaEnroll}
          ctaCurriculum={ctaCurriculum}
          ctaEnrollUrl={ctaEnrollUrl}
          ctaCurriculumUrl={ctaCurriculumUrl}
          highlights={highlights}
        />
      ),
    },
    NonFsdsLpOverviewSection: {
      label: 'LP Overview',
      defaultProps: {
        title: "How you'll learn",
        description: 'Multiple learning methods designed for working professionals',
        methods: [
          {
            icon: 'Video',
            title: 'Live Zoom Sessions',
            description: 'Interact directly with instructors from top companies in real-time sessions.',
          },
          {
            icon: 'BookOpen',
            title: 'Smart Review System',
            description:
              'Watch recordings, complete quizzes and code homework, all auto-graded with AI-assisted review.',
          },
          {
            icon: 'Code2',
            title: 'Hands-on Sandbox',
            description: 'Deploy industry-ready projects on pre-configured environments — no setup required.',
          },
          {
            icon: 'MessageCircle',
            title: 'Dedicated Teaching Assistants',
            description: 'Get continuous support throughout your learning journey from experienced TAs.',
          },
          {
            icon: 'FileText',
            title: 'Blogs & Webinars',
            description: 'Stay up-to-date with the latest data science and AI trends and best practices.',
          },
        ],
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        description: { label: 'Description', type: 'textarea' },
        methods: {
          label: 'Methods',
          type: 'array',
          arrayFields: {
            icon: {
              label: 'Icon',
              type: 'select',
              options: [
                { label: 'Video', value: 'Video' },
                { label: 'Book', value: 'BookOpen' },
                { label: 'Code', value: 'Code2' },
                { label: 'Chat', value: 'MessageCircle' },
                { label: 'Document', value: 'FileText' },
              ],
            },
            title: { label: 'Title', type: 'text' },
            description: { label: 'Description', type: 'textarea' },
          },
          getItemSummary: (item: Record<string, string>) => item.title || 'Method',
        },
      },
      render: ({ title, description, methods, puck: _puck }) => (
        <NonFsdsLpOverviewSection title={title} description={description} methods={methods} />
      ),
    },
    NonFsdsLpCoursePathSection: {
      label: 'LP Course Path',
      defaultProps: {
        title: 'Your learning journey',
        subtitle: 'From zero to Full Stack Data Scientist — designed for real-world impact',
        courses: [
          {
            code: 'DSAI',
            title: 'Data Science & AI',
            duration: '6 months',
            level: 'Beginner → Intermediate',
            description: [
              'Build foundational math skills then master modeling.',
              'Core modules: Machine Learning, Deep Learning.',
              'No prerequisites required.',
            ],
            skills: ['Python', 'scikit-learn', 'TensorFlow', 'Statistics', 'Feature Engineering'],
            curriculumUrl: '#',
          },
        ],
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        courses: {
          label: 'Courses',
          type: 'array',
          defaultItemProps: {
            code: 'NEW',
            title: 'New Course',
            duration: '3 months',
            level: 'Beginner',
            description: ['Course description goes here.'],
            skills: ['Skill 1'],
            curriculumUrl: '#',
          },
          arrayFields: {
            code: { label: 'Code', type: 'text' },
            title: { label: 'Title', type: 'text' },
            duration: { label: 'Duration', type: 'text' },
            level: { label: 'Level', type: 'text' },
            curriculumUrl: { label: 'Curriculum URL', type: 'text' },
          },
          getItemSummary: (item: Record<string, string>) => `${item.code} — ${item.title}` || 'Course',
        },
      },
      render: ({ title, subtitle, courses, puck: _puck }) => (
        <NonFsdsLpCoursePathSection title={title} subtitle={subtitle} courses={courses} />
      ),
    },
    NonFsdsLpProjectsSection: {
      label: 'LP Projects',
      defaultProps: {
        title: 'Portfolio-ready projects',
        description: 'Build real-world projects that demonstrate your skills to employers',
        projects: [
          {
            title: 'Customer Churn Prediction',
            description: 'Build ML models to identify at-risk customers.',
            skills: ['Classification', 'Feature Engineering', 'Business Analytics'],
          },
          {
            title: 'Sales Forecasting Dashboard',
            description: 'End-to-end time series forecasting pipeline.',
            skills: ['Time Series', 'Dashboard', 'Data Engineering'],
          },
        ],
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        description: { label: 'Description', type: 'textarea' },
        projects: {
          label: 'Projects',
          type: 'array',
          defaultItemProps: {
            title: 'New Project',
            description: 'Project description goes here.',
            skills: ['Skill 1'],
          },
          arrayFields: {
            title: { label: 'Title', type: 'text' },
            description: { label: 'Description', type: 'textarea' },
          },
          getItemSummary: (item: Record<string, string>) => item.title || 'Project',
        },
      },
      render: ({ title, description, projects, puck: _puck }) => (
        <NonFsdsLpProjectsSection title={title} description={description} projects={projects} />
      ),
    },
    NonFsdsLpTestimonialsSection: {
      label: 'LP Testimonials',
      defaultProps: {
        title: 'What graduates say',
        subtitle: 'Trusted by learners who transformed their careers through structured guidance',
        testimonials: [
          {
            quote: 'The weekly mentor feedback helped me fix real project issues fast.',
            name: 'Minh Tran',
            role: 'Data Engineer',
            avatar: '/images/avatar.png',
          },
          {
            quote: 'I stopped learning in isolation and started improving with a real peer network.',
            name: 'Linh Nguyen',
            role: 'ML Engineer',
            avatar: '/images/avatar.png',
          },
        ],
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        testimonials: {
          label: 'Testimonials',
          type: 'array',
          arrayFields: {
            quote: { label: 'Quote', type: 'textarea' },
            name: { label: 'Name', type: 'text' },
            role: { label: 'Role', type: 'text' },
            avatar: { label: 'Avatar', type: 'custom', render: ImageFieldRender },
          },
          getItemSummary: (item: Record<string, string>) => item.name || 'Testimonial',
        },
      },
      render: ({ title, subtitle, testimonials, puck: _puck }) => (
        <NonFsdsLpTestimonialsSection title={title} subtitle={subtitle} testimonials={testimonials} />
      ),
    },
    NonFsdsLpFaqSection: {
      label: 'LP FAQ',
      defaultProps: {
        title: 'Frequently asked questions',
        subtitle: 'Everything you need to know about the learning path',
        faqs: [
          {
            id: 'lp-faq-1',
            q: 'How is this different from self-paced video courses?',
            a: 'You get live mentor guidance, structured milestones, and project reviews.',
          },
        ],
        isEnglish: true,
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        faqs: {
          label: 'FAQs',
          type: 'array',
          arrayFields: {
            id: { label: 'ID', type: 'text' },
            q: { label: 'Question', type: 'text' },
            a: { label: 'Answer', type: 'textarea' },
          },
          getItemSummary: (item: Record<string, string>) => item.q || 'Question',
        },
      },
      render: ({ title, subtitle, faqs, isEnglish, puck: _puck }) => (
        <NonFsdsLpFaqSection title={title} subtitle={subtitle} faqs={faqs} isEnglish={isEnglish ?? true} />
      ),
    },
    NonFsdsLpCtaSection: {
      label: 'LP CTA',
      defaultProps: {
        title: 'Ready to start your data science journey?',
        description: 'Join 20,000+ learners already transforming their careers with structured guidance.',
        buttonText: 'Enroll Now',
        buttonUrl: TEMPLATE_DEFAULT_URLS.myCourses,
      },
      fields: {
        title: { label: 'Title', type: 'textarea' },
        description: { label: 'Description', type: 'textarea' },
        buttonText: { label: 'Button Text', type: 'text' },
        buttonUrl: { label: 'Button Link URL', type: 'text' },
      },
      render: ({ title, description, buttonText, buttonUrl, puck: _puck }) => (
        <NonFsdsLpCtaSection
          title={title}
          description={description}
          buttonText={buttonText}
          buttonUrl={buttonUrl}
        />
      ),
    },
    NonFsdsSignInSection: {
      label: 'Sign In',
      defaultProps: {
        heroTitle: 'Unlock Your\nPotential',
        heroDescription:
          'Sign in to explore your personalized learning journey, track your progress, and connect with peers.',
        formTitle: 'Welcome Back',
        formSubtitle: 'Please enter your details to sign in.',
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        signUpText: 'Sign up',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signUpUrl: TEMPLATE_DEFAULT_URLS.signUp,
        gradientFrom: '#1d4ed8',
        gradientTo: '#1e3a8a',
      },
      fields: {
        heroTitle: { label: 'Hero Title', type: 'textarea' },
        heroDescription: { label: 'Hero Description', type: 'textarea' },
        formTitle: { label: 'Form Title', type: 'text' },
        formSubtitle: { label: 'Form Subtitle', type: 'text' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        signUpText: { label: 'Sign Up Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signUpUrl: { label: 'Sign Up Link URL', type: 'text' },
        gradientFrom: { label: 'Gradient Start', type: 'custom', render: ColorFieldRender },
        gradientTo: { label: 'Gradient End', type: 'custom', render: ColorFieldRender },
      },
      render: ({
        heroTitle,
        heroDescription,
        formTitle,
        formSubtitle,
        logoUrl,
        signUpText,
        brandHref,
        signUpUrl,
        gradientFrom,
        gradientTo,
        puck: _puck,
      }) => (
        <NonFsdsSignInSection
          heroTitle={heroTitle}
          heroDescription={heroDescription}
          formTitle={formTitle}
          formSubtitle={formSubtitle}
          logoUrl={logoUrl}
          signUpText={signUpText}
          brandHref={brandHref}
          signUpUrl={signUpUrl}
          gradientFrom={gradientFrom}
          gradientTo={gradientTo}
        />
      ),
    },
    NonFsdsSignUpSection: {
      label: 'Sign Up',
      defaultProps: {
        heroTitle: 'Start Your\nJourney',
        heroDescription:
          'Create an account to gain access to world-class curriculum and kickstart your learning experience today.',
        formTitle: 'Create an Account',
        formSubtitle: 'Please enter your details below to sign up.',
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        signInText: 'Log in',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
        gradientFrom: '#1e3a8a',
        gradientTo: '#1d4ed8',
      },
      fields: {
        heroTitle: { label: 'Hero Title', type: 'textarea' },
        heroDescription: { label: 'Hero Description', type: 'textarea' },
        formTitle: { label: 'Form Title', type: 'text' },
        formSubtitle: { label: 'Form Subtitle', type: 'text' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        signInText: { label: 'Sign In Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Sign In Link URL', type: 'text' },
        gradientFrom: { label: 'Gradient Start', type: 'custom', render: ColorFieldRender },
        gradientTo: { label: 'Gradient End', type: 'custom', render: ColorFieldRender },
      },
      render: ({
        heroTitle,
        heroDescription,
        formTitle,
        formSubtitle,
        logoUrl,
        signInText,
        brandHref,
        signInUrl,
        gradientFrom,
        gradientTo,
        puck: _puck,
      }) => (
        <NonFsdsSignUpSection
          heroTitle={heroTitle}
          heroDescription={heroDescription}
          formTitle={formTitle}
          formSubtitle={formSubtitle}
          logoUrl={logoUrl}
          signInText={signInText}
          brandHref={brandHref}
          signInUrl={signInUrl}
          gradientFrom={gradientFrom}
          gradientTo={gradientTo}
        />
      ),
    },
    NonFsdsForgotPasswordSection: {
      label: 'Forgot Password',
      defaultProps: {
        title: 'Forgot Password',
        subtitle: "No worries, we'll send you reset instructions.",
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        backLinkText: 'Back to sign in',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        backLinkText: { label: 'Back Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Back Link URL', type: 'text' },
      },
      render: ({ title, subtitle, logoUrl, backLinkText, brandHref, signInUrl, puck: _puck }) => (
        <NonFsdsForgotPasswordSection
          title={title}
          subtitle={subtitle}
          logoUrl={logoUrl}
          backLinkText={backLinkText}
          brandHref={brandHref}
          signInUrl={signInUrl}
        />
      ),
    },
    NonFsdsEmailVerificationSection: {
      label: 'Email Verification',
      defaultProps: {
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
        errorButtonText: 'Back to Sign In',
        successButtonText: 'Proceed to Sign In',
      },
      fields: {
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Sign In Button URL', type: 'text' },
        errorButtonText: { label: 'Error State Button Text', type: 'text' },
        successButtonText: { label: 'Success State Button Text', type: 'text' },
      },
      render: ({ logoUrl, brandHref, signInUrl, errorButtonText, successButtonText, puck: _puck }) => (
        <NonFsdsEmailVerificationSection
          logoUrl={logoUrl}
          brandHref={brandHref}
          signInUrl={signInUrl}
          errorButtonText={errorButtonText}
          successButtonText={successButtonText}
        />
      ),
    },
    NonFsdsVerifyAccountSection: {
      label: 'Verify Account',
      defaultProps: {
        title: 'Verify your account',
        subtitle:
          'We sent a verification link to your email. If you do not see anything, please click the button below.',
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        resendButtonText: 'Resend verification email',
        backLinkText: 'Back to sign in',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        resendButtonText: { label: 'Resend Button Text', type: 'text' },
        backLinkText: { label: 'Back Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Back Link URL', type: 'text' },
      },
      render: ({ title, subtitle, logoUrl, resendButtonText, backLinkText, brandHref, signInUrl, puck: _puck }) => (
        <NonFsdsVerifyAccountSection
          title={title}
          subtitle={subtitle}
          logoUrl={logoUrl}
          resendButtonText={resendButtonText}
          backLinkText={backLinkText}
          brandHref={brandHref}
          signInUrl={signInUrl}
        />
      ),
    },
    NonFsdsResetPasswordSection: {
      label: 'Reset Password',
      defaultProps: {
        title: 'Set new password',
        subtitle: 'Your new password must be different to previously used passwords.',
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        backLinkText: 'Back to sign in',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        backLinkText: { label: 'Back Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Back Link URL', type: 'text' },
      },
      render: ({ title, subtitle, logoUrl, backLinkText, brandHref, signInUrl, puck: _puck }) => (
        <NonFsdsResetPasswordSection
          title={title}
          subtitle={subtitle}
          logoUrl={logoUrl}
          backLinkText={backLinkText}
          brandHref={brandHref}
          signInUrl={signInUrl}
        />
      ),
    },
    NonFsdsPasswordResetSuccessfulSection: {
      label: 'Password Reset Success',
      defaultProps: {
        title: 'Password Set Successfully!',
        subtitle: 'Your password has been successfully set. You can now log in using your new credentials.',
        logoUrl: NON_FSDS_DEFAULT_LOGO_URL,
        loginButtonText: 'Log In',
        backLinkText: 'Back to log in',
        brandHref: TEMPLATE_DEFAULT_URLS.home,
        signInUrl: TEMPLATE_DEFAULT_URLS.signIn,
      },
      fields: {
        title: { label: 'Title', type: 'text' },
        subtitle: { label: 'Subtitle', type: 'textarea' },
        logoUrl: { label: 'Logo', type: 'custom', render: ImageFieldRender },
        loginButtonText: { label: 'Login Button Text', type: 'text' },
        backLinkText: { label: 'Back Link Text', type: 'text' },
        brandHref: { label: 'Logo Link URL', type: 'text' },
        signInUrl: { label: 'Sign In Link URL', type: 'text' },
      },
      render: ({ title, subtitle, logoUrl, loginButtonText, backLinkText, brandHref, signInUrl, puck: _puck }) => (
        <NonFsdsPasswordResetSuccessfulSection
          title={title}
          subtitle={subtitle}
          logoUrl={logoUrl}
          loginButtonText={loginButtonText}
          backLinkText={backLinkText}
          brandHref={brandHref}
          signInUrl={signInUrl}
        />
      ),
    },
    ConsultationForm: {
      label: 'Consultation Form',
      defaultProps: generateDefaultProps('ConsultationForm'),
      fields: generatePuckFields('ConsultationForm'),
      render: ({
        className,
        heading,
        headingColor,
        bgGradientFrom,
        bgGradientTo,
        minHeight,
        showRegisterButton,
        registerButtonUrl,
        registerButtonText,
        puck: _puck,
      }) => (
        <ConsultationForm
          className={className}
          heading={heading}
          headingColor={headingColor}
          bgGradientFrom={bgGradientFrom}
          bgGradientTo={bgGradientTo}
          minHeight={minHeight}
          showRegisterButton={showRegisterButton}
          registerButtonUrl={registerButtonUrl}
          registerButtonText={registerButtonText}
        />
      ),
    },
    ...generalV1PuckComponents,
  },
}
